// Body mass index, derived from weight and height (plan §2.1, §5.2).
//
// BMI is computed whenever it is shown and never stored, so a corrected weight can't
// leave a stale BMI behind. Only the arithmetic lives here: kg ÷ m², to one decimal,
// rounded half up, in integers. No category (underweight, obese) is attached, because
// categories are clinical thresholds and, for children, need BMI-for-age z-scores
// (plan §13, C4; TODO(clinical-verify)).

import { VITAL_SIGNS } from './definitions.ts';

/** BMI is shown to one decimal place. */
export const BMI_DECIMALS = 1;

/**
 * BMI × 10 (one decimal), from weight in kg × 100 and height in cm × 10, the scaled forms
 * of `body_weight` and `body_height`. `null` when either is missing or zero.
 */
export function bmiFromScaled(
  weightScaled: number | null,
  heightScaled: number | null,
): number | null {
  if (weightScaled === null || heightScaled === null) return null;
  for (const value of [weightScaled, heightScaled]) {
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new RangeError('Weight and height must be non-negative whole numbers of units');
    }
  }
  if (weightScaled === 0 || heightScaled === 0) return null;
  // weight kg = w / 10^wd; height m = h / (10^hd × 100); BMI × 10^bd
  //   = w × 10^(2·hd + 4 + bd − wd) / h²
  const exponent =
    2 * VITAL_SIGNS.body_height.decimals + 4 + BMI_DECIMALS - VITAL_SIGNS.body_weight.decimals;
  const numerator = BigInt(weightScaled) * 10n ** BigInt(exponent);
  const denominator = BigInt(heightScaled) * BigInt(heightScaled);
  // Round half up: floor((2n + d) / 2d).
  const rounded = (2n * numerator + denominator) / (2n * denominator);
  return Number(rounded);
}

/** BMI × 10 as text, e.g. 242 → "24.2". */
export function formatBmi(bmiScaled: number): string {
  if (!Number.isSafeInteger(bmiScaled) || bmiScaled < 0) {
    throw new RangeError('BMI must be a non-negative whole number of tenths');
  }
  return `${String(Math.trunc(bmiScaled / 10))}.${String(bmiScaled % 10)}`;
}
